import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'

export interface PathStepTask {
  id: number
  section?: {
    id: number
    name: string
  } | null
  name: string
  url: string
  details: string
  airtable_id?: number | null
  airtable_order?: number | null
  created_datetime?: string
  pathmaker?: number
  review_required?: boolean
  fellow_reportable?: boolean
  has_external_dependency?: boolean

  assigned_mentor?: number | null
}

export interface PathStep {
  id: number
  task: PathStepTask
  week_order: number
  order_within_week: number
  is_required: boolean
  points: number
  created_at: string
  path: number
  has_external_dependency: boolean
}

export interface CreatePathStepPayload {
  task_data: {
    name: string
    url?: string
    details?: string
    section?: number | null
    review_required?: boolean
    fellow_reportable?: boolean
  }
  week_order: number
  order_within_week?: number | null
  is_required: boolean
  points: number
}

export const usePathStepsStore = defineStore('pathSteps', () => {
  const isUpdating = ref(false)
  const error = ref<string | null>(null)
  const isDeleting = ref(false)
  const isCreating = ref(false)

  const createPathStepWithTask = async (pathId: number, payload: CreatePathStepPayload) => {
    isCreating.value = true
    error.value = null

    try {
      const response = await api.post(API_ROUTES.CREATE_PATH_STEP_WITH_TASK(pathId), payload)
      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to create path step'
      console.error('Error creating path step:', err)
      return null
    } finally {
      isCreating.value = false
    }
  }

  const updatePathStep = async (stepId: number, updatedStep: Partial<PathStep>) => {
    isUpdating.value = true
    error.value = null

    try {
      console.log('Store: Sending path step update to API:', { stepId, updatedStep })
      await api.put(API_ROUTES.UPDATE_PATH_STEP(stepId), updatedStep)
      return true
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update path step'
      console.error('Error updating path step:', err)
      return false
    } finally {
      isUpdating.value = false
    }
  }

  const deletePathStep = async (stepId: number) => {
    isDeleting.value = true
    error.value = null

    try {
      await api.delete(API_ROUTES.DELETE_PATH_STEP(stepId))
      return true
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to delete path step'
      console.error('Error deleting path step:', err)
      return false
    } finally {
      isDeleting.value = false
    }
  }

  return {
    isUpdating,
    error,
    isCreating,
    createPathStepWithTask,
    updatePathStep,
    deletePathStep,
    isDeleting
  }
})
